import React from "react";
import { Box } from "@mui/material";
import { useFormState } from "react-final-form";
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import formatDollar from "../../../_src_shared/utils/functions/formatDollar";
import colors, { status } from "../../../theme/colors";
import { APP_TOKENS } from "../../../components/publicForm/publicFormTokens";

/**
 * Live composition of a construction request: initial loan + financed draws,
 * against the full project budget.
 *
 * Construction products ask for three amounts whose relationship is the thing
 * that actually matters — and reading three separate dollar boxes does not tell
 * an applicant whether their draws cover the build. This shows the total, the
 * split, and the out-of-pocket remainder as they type.
 *
 * Reads live form state; renders nothing until at least one amount is entered,
 * so an untouched step stays quiet.
 */

// Whole dollars: cents are noise at this scale and make the bar labels wrap.
const dollars = (n) => formatDollar(n, "$0", { fractionDigits: 0 });

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const LegendItem = ({ swatch, label, value }) => (
  <Flx centerY gap={0.875}>
    <Box
      sx={{ width: 10, height: 10, borderRadius: "3px", flex: "none", ...swatch }}
    />
    <Txt component="div" variant="caption" sx={{ color: colors.slate600 }}>
      {label}{" "}
      <Txt
        component="span"
        sx={{
          color: colors.slate900,
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </Txt>
    </Txt>
  </Flx>
);

const SWATCH = {
  initial: { backgroundColor: colors.action },
  draws: { backgroundColor: colors.teal },
  gap: {
    backgroundImage: `repeating-linear-gradient(-45deg, ${colors.slate300} 0 3px, ${colors.slate100} 3px 6px)`,
  },
};

const Segment = ({ percent, label, sx, labelColor = "#fff" }) => {
  if (percent <= 0) return null;
  return (
    <Box
      sx={{
        width: `${percent}%`,
        height: "100%",
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "width .35s cubic-bezier(.4,0,.2,1)",
        ...sx,
      }}
    >
      {/* Below ~14% the label is wider than its own segment and turns into an
          ellipsis; the legend already names every colour. */}
      {percent > 14 && (
        <Txt
          component="div"
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: labelColor,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            px: 1,
          }}
        >
          {label}
        </Txt>
      )}
    </Box>
  );
};

const LoanBreakdownPanel = () => {
  const { values } = useFormState({ subscription: { values: true } });

  const initial = toNumber(values?.baseLoanAmount);
  const draws = toNumber(values?.totalHoldback);
  const budget = toNumber(values?.totalConstructionBudget);
  const totalLoan = initial + draws;

  if (totalLoan === 0 && budget === 0) return null;

  // A budget under the draw request is a data-entry mistake, not a funding gap —
  // say so rather than rendering a nonsensical negative remainder.
  const budgetBelowDraws = budget > 0 && draws > 0 && budget < draws;
  const outOfPocket = budget > totalLoan ? budget - totalLoan : 0;

  // Scale against whichever is larger so the bar always represents the full
  // project, not just the financed part.
  const scale = Math.max(totalLoan, budget, 1);
  const pct = (n) => (n / scale) * 100;

  const coverage =
    budget > 0 && draws > 0 && !budgetBelowDraws
      ? Math.min(100, Math.round((draws / budget) * 100))
      : null;

  let note = null;
  if (budgetBelowDraws) {
    note = {
      tone: status.error,
      text: "The construction budget is lower than the financed budget — double-check these amounts.",
    };
  } else if (coverage !== null && coverage >= 100) {
    note = {
      tone: status.success,
      text: "Draws cover 100% of the construction budget — no out-of-pocket construction costs.",
    };
  } else if (coverage !== null) {
    note = {
      tone: status.warning,
      text: `Draws cover ${coverage}% of the construction budget. You'll fund the remaining ${dollars(budget - draws)} out of pocket.`,
    };
  }

  return (
    <Box
      aria-live="polite"
      sx={{
        border: `1px solid ${APP_TOKENS.border}`,
        borderRadius: "12px",
        backgroundColor: APP_TOKENS.canvas,
        p: 2.5,
      }}
    >
      <Flx between centerY wrap gap={1.5} sx={{ mb: 1.75 }}>
        <Txt
          component="div"
          sx={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: colors.slate600,
          }}
        >
          Loan breakdown
        </Txt>
        <Txt component="div" variant="body2" sx={{ color: colors.slate600 }}>
          Total loan request{" "}
          <Txt
            component="span"
            sx={{
              fontSize: 17,
              fontWeight: 800,
              color: colors.slate900,
              fontVariantNumeric: "tabular-nums",
              letterSpacing: "-0.01em",
            }}
          >
            {dollars(totalLoan)}
          </Txt>
        </Txt>
      </Flx>

      <Box
        role="img"
        aria-label={`Initial loan ${dollars(initial)}, financed draws ${dollars(draws)}${outOfPocket > 0 ? `, out of pocket ${dollars(outOfPocket)}` : ""}`}
        sx={{
          height: 34,
          borderRadius: "8px",
          overflow: "hidden",
          display: "flex",
          backgroundColor: colors.slate200,
        }}
      >
        <Segment percent={pct(initial)} label="Initial" sx={SWATCH.initial} />
        <Segment percent={pct(draws)} label="Draws" sx={SWATCH.draws} />
        <Segment
          percent={pct(outOfPocket)}
          label="Out of pocket"
          sx={SWATCH.gap}
          labelColor={colors.slate600}
        />
      </Box>

      <Flx wrap gap={2.25} sx={{ mt: 1.5 }}>
        <LegendItem
          swatch={SWATCH.initial}
          label="Initial loan"
          value={dollars(initial)}
        />
        <LegendItem
          swatch={SWATCH.draws}
          label="Financed budget"
          value={dollars(draws)}
        />
        {outOfPocket > 0 && (
          <LegendItem
            swatch={SWATCH.gap}
            label="Out of pocket"
            value={dollars(outOfPocket)}
          />
        )}
      </Flx>

      {note && (
        <Box
          sx={{
            mt: 1.75,
            px: 1.5,
            py: 1.125,
            borderRadius: "8px",
            backgroundColor: note.tone.bg,
          }}
        >
          <Txt
            component="div"
            variant="body2"
            sx={{ color: note.tone.fg, lineHeight: 1.45 }}
          >
            {note.text}
          </Txt>
        </Box>
      )}
    </Box>
  );
};

export default LoanBreakdownPanel;
